import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useNotify, useRefresh, useTranslate } from 'react-admin';
import {
  regenerateRelayDeposit,
  RELAY_DEPOSIT_CHAIN_OPTIONS,
  RelayDepositRecord,
} from '../../services/relayDepositService';
import { PermissionButton } from 'src/routes/components/permission-button';

type Props = {
  record: RelayDepositRecord;
  open: boolean;
  onClose: () => void;
};

export default function RegenerateDialog({ record, open, onClose }: Props) {
  const t = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [chainId, setChainId] = useState<number>(8453);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const res = await regenerateRelayDeposit({ id: record.id, chainId });
      notify(res?.message || t('module.relay-deposit.regenerateSuccess'), { type: 'success' });
      refresh();
      onClose();
    } catch (error: any) {
      const msg =
        error?.body?.message ||
        error?.message ||
        t('module.relay-deposit.regenerateFailed');
      notify(msg, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('module.relay-deposit.regenerateTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('module.relay-deposit.regenerateHint')}
          </Typography>
          <Typography variant="caption" display="block">
            ID: {record.id}
          </Typography>
          <FormControl fullWidth>
            <InputLabel id="relay-regenerate-chain-label">
              {t('module.relay-deposit.chainId')}
            </InputLabel>
            <Select
              labelId="relay-regenerate-chain-label"
              label={t('module.relay-deposit.chainId')}
              value={chainId}
              onChange={(e) => setChainId(Number(e.target.value))}
            >
              {RELAY_DEPOSIT_CHAIN_OPTIONS.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                  {opt.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t('ra.action.cancel')}
        </Button>
        <PermissionButton resource="relay" action="update">
          <Button variant="contained" onClick={handleSubmit} disabled={loading}>
            {t('module.relay-deposit.regenerate')}
          </Button>
        </PermissionButton>
      </DialogActions>
    </Dialog>
  );
}
